import type { Thread, ThreadDetail } from '@central/api';
import { Card, StatusChip, color, font, formatAgo, formatDateTime } from '@central/ui';
import { Alert, Button } from '@mantine/core';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { keys } from '../api';
import { QueryState } from '../components/parts';
import { ThreadWatchers } from '../components/ThreadWatchers';
import { errorMessage } from '../lib/errors';
import { inboxListQuery, inboxViews, kindChip, type InboxListSearch } from '../lib/inbox';
import { muted } from '../lib/labels';
import { useIsReseller } from '../lib/me';
import { threadDetailOptions, threadListOptions, useThreadsApi } from '../lib/threads';
import { PriorityChip, TierChip } from './inboxParts';
import { ThreadControls } from './TicketControls';
import { Composer, CrashReports, MessageItem } from './TicketMessages';
import { TicketBanner, TicketOrganizer } from './TicketOrganize';

/**
 * One support ticket: the conversation beside a details sidebar. The search
 * carries the inbox list it was opened from, for the back link and for
 * previous and next.
 */
export function Ticket() {
  const { threadId } = useParams({ from: '/app/inbox/$threadId' });
  const search = useSearch({ from: '/app/inbox/$threadId' });
  return (
    <>
      <TicketTopBar id={threadId} search={search} />
      {/* Keyed so a draft never outlives its thread: a cached thread renders without a
          loading state, which would otherwise keep the same Composer mounted. */}
      <ThreadPane key={threadId} id={threadId} />
    </>
  );
}

const stepStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 8,
  border: `1px solid ${color.border}`,
  background: color.surface,
  color: color.text,
  fontSize: 18,
  textDecoration: 'none',
};

/**
 * The way back to the list, the ticket's reference, and previous and next
 * within the list when that list is already loaded; it is never fetched for this.
 */
function TicketTopBar({ id, search }: { id: string; search: InboxListSearch }) {
  const reseller = useIsReseller();
  const threads = useThreadsApi();
  const { view, listQuery } = inboxListQuery(search, reseller);
  const list = useInfiniteQuery({ ...threadListOptions(threads, listQuery), enabled: false });
  const detail = useInfiniteQuery(threadDetailOptions(threads, id));
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const at = items.findIndex((t) => t.id === id);
  const ref = detail.data?.pages[0]?.thread.ref ?? items[at]?.ref;
  const listName = search.q ? 'Search' : inboxViews[view].label;
  const step = (t: Thread | undefined, label: string, glyph: string) =>
    t ? (
      <Link
        to="/inbox/$threadId"
        params={{ threadId: t.id }}
        search={search}
        aria-label={label}
        style={stepStyle}
      >
        <span aria-hidden>{glyph}</span>
      </Link>
    ) : (
      <button
        type="button"
        disabled
        aria-label={label}
        style={{ ...stepStyle, color: color.textMuted, opacity: 0.5 }}
      >
        <span aria-hidden>{glyph}</span>
      </button>
    );
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
        minHeight: 40,
      }}
    >
      <nav
        aria-label="Breadcrumb"
        style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, minWidth: 0 }}
      >
        <Link
          to="/inbox"
          search={search}
          style={{
            color: color.link,
            textDecoration: 'none',
            minHeight: 40,
            display: 'inline-flex',
            alignItems: 'center',
          }}
        >
          <span aria-hidden>←&nbsp;</span>
          {reseller ? 'Support inbox' : 'Inbox'} · {listName}
        </Link>
        {ref && (
          <>
            <span aria-hidden style={muted}>
              /
            </span>
            <span style={{ fontFamily: font.mono, fontWeight: 600 }}>{ref}</span>
          </>
        )}
      </nav>
      {at >= 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {step(items[at - 1], 'Previous ticket', '‹')}
          <span style={{ fontSize: 13, ...muted }}>
            {at + 1} of {items.length}
          </span>
          {step(items[at + 1], 'Next ticket', '›')}
        </div>
      )}
    </div>
  );
}

/** The thread, marked read on open, with earlier messages loaded on request. */
function ThreadPane({ id }: { id: string }) {
  const threads = useThreadsApi();
  const qc = useQueryClient();
  const { data, error, isPending, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(threadDetailOptions(threads, id));
  const [earlierError, setEarlierError] = useState<unknown>();
  const loadEarlier = async () => {
    setEarlierError(undefined);
    const r = await fetchNextPage();
    if (r.isFetchNextPageError) setEarlierError(r.error);
  };
  const unread = data?.pages[0]?.thread.unread ?? 0;
  const { mutate: markRead } = useMutation({
    mutationFn: () => threads.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.threads }),
  });
  useEffect(() => {
    if (unread > 0) markRead();
  }, [unread, markRead]);
  return (
    <QueryState isPending={isPending} error={error}>
      {() => (
        <ThreadBody
          d={{
            ...data!.pages[0]!,
            messages: data!.pages.toReversed().flatMap((p) => p.messages),
          }}
          earlier={
            hasNextPage && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Button
                  variant="default"
                  size="xs"
                  style={{ alignSelf: 'center' }}
                  loading={isFetchingNextPage}
                  onClick={() => void loadEarlier()}
                >
                  Load earlier messages
                </Button>
                {earlierError !== undefined && (
                  <Alert color="red">{errorMessage(earlierError)}</Alert>
                )}
              </div>
            )
          }
        />
      )}
    </QueryState>
  );
}

function ThreadBody({ d, earlier }: { d: ThreadDetail; earlier: ReactNode }) {
  const t = d.thread;
  const reseller = useIsReseller();
  return (
    <>
      <TicketHeader t={t} />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
        <div
          style={{
            flex: '999 1 520px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <TicketBanner d={d} />
          {t.startedBy === 'system' && !reseller && (
            <Card>
              <CrashReports threadId={t.id} customerId={t.customer.id} />
            </Card>
          )}
          <Card>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {earlier}
              <ol
                aria-label="Messages"
                style={{
                  listStyle: 'none',
                  margin: 0,
                  padding: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 14,
                }}
              >
                {d.messages.map((m) => (
                  <MessageItem key={m.id} m={m} customerName={t.customer.name} />
                ))}
              </ol>
              {/* A merged ticket takes no post: its conversation continues in the ticket it went into. */}
              {!d.mergedInto && <Composer t={t} />}
            </div>
          </Card>
        </div>
        <aside
          aria-label="Ticket details"
          style={{
            flex: '1 1 280px',
            maxWidth: 340,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <SideCard title="Properties">
            <ThreadControls t={t} />
          </SideCard>
          <SideCard title="Dates">
            <TicketDates t={t} />
          </SideCard>
          {!reseller && (
            <SideCard title="Organise">
              <TicketOrganizer d={d} />
            </SideCard>
          )}
          {!reseller && (
            <SideCard title="Watchers">
              <ThreadWatchers t={t} />
            </SideCard>
          )}
        </aside>
      </div>
    </>
  );
}

/** A sidebar card under a small uppercase heading. */
function SideCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card style={{ width: '100%' }}>
      <h2
        style={{
          margin: '0 0 12px',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          ...muted,
        }}
      >
        {title}
      </h2>
      {children}
    </Card>
  );
}

/** Kind, priority and tier; when it was opened; the subject; and what it is about. */
function TicketHeader({ t }: { t: Thread }) {
  const reseller = useIsReseller();
  const chip = kindChip[t.kind];
  const crashes =
    t.crashCount !== undefined && ` · ${t.crashCount} crash${t.crashCount === 1 ? '' : 'es'}`;
  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
        <PriorityChip p={t.priority} />
        <TierChip t={t} />
        <span style={{ fontSize: 13, ...muted }}>
          {t.startedBy === 'system' ? 'Sent automatically' : 'Opened'} · {formatAgo(t.createdAt)}
        </span>
      </div>
      <h1 style={{ margin: 0, fontSize: 22, overflowWrap: 'anywhere' }}>{t.subject}</h1>
      {reseller ? (
        <div style={{ fontSize: 13, ...muted }}>
          <Link to="/partner/customers/$customerId" params={{ customerId: t.customer.id }}>
            {t.customer.name}
          </Link>
          {t.environment && (
            <>
              {' · '}
              <Link
                to="/partner/environments/$environmentId"
                params={{ environmentId: t.environment.id }}
              >
                {t.environment.name}
              </Link>
            </>
          )}
          {t.area && ` · ${t.area}`}
          {crashes}
        </div>
      ) : (
        <div style={{ fontSize: 13, ...muted }}>
          <Link to="/customers/$customerId" params={{ customerId: t.customer.id }}>
            {t.customer.name}
          </Link>
          {t.environment && (
            <>
              {' · '}
              <Link to="/environments/$environmentId" params={{ environmentId: t.environment.id }}>
                {t.environment.name}
              </Link>
              {/* A crash report's diagnostics link its own log window instead. */}
              {t.startedBy !== 'system' && (
                <>
                  {' · '}
                  <Link
                    to="/logs"
                    search={{ customerId: t.customer.id, environmentId: t.environment.id }}
                  >
                    Open in Logs
                  </Link>
                </>
              )}
            </>
          )}
          {t.area && ` · ${t.area}`}
          {crashes}
        </div>
      )}
    </header>
  );
}

const personLabel = (a: Thread['reportedBy'], customerName: string) =>
  a.kind === 'staff'
    ? `${a.name ?? 'Rethink'} (Rethink)`
    : a.kind === 'system'
      ? (a.name ?? 'GoATMS Central')
      : `${a.name ?? 'Customer'} (${customerName})`;

/** Who reported the ticket and when, and who resolved it and when. */
function TicketDates({ t }: { t: Thread }) {
  return (
    <dl
      style={{
        margin: 0,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        columnGap: 12,
        rowGap: 6,
        fontSize: 13,
      }}
    >
      <dt style={muted}>Reported</dt>
      <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
        {formatDateTime(t.createdAt)} by {personLabel(t.reportedBy, t.customer.name)}
      </dd>
      <dt style={muted}>Resolved</dt>
      <dd style={{ margin: 0, minWidth: 0, overflowWrap: 'anywhere' }}>
        {t.resolvedAt && t.resolvedBy
          ? `${formatDateTime(t.resolvedAt)} by ${personLabel(t.resolvedBy, t.customer.name)}`
          : 'Not resolved'}
      </dd>
    </dl>
  );
}
